import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../contexts/Auth";
import { useNavigate } from "react-router-dom";
import api from "../helpers/api";
import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import ForgotPassword from "../components/ForgotPassword";
import RequestVerification from "../components/RequestVerification";
import AppTheme from "../shared-theme/AppTheme";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Banner from "../components/Banner";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const GradientBackground = styled("div")({
  position: "fixed",
  inset: 0,
  background: "radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)",
  zIndex: -1,
});

const SignInContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100dvh",
  padding: theme.spacing(2),
  overflowY: "auto",
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

const Login = (props: { disableCustomTheme?: boolean }) => {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationError, setVerificationError] = React.useState(false);
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState("");
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleVerifyOpen = () => {
    setVerificationError(false);
    setVerifyDialogOpen(true);
  };

  const handleVerifyClose = () => {
    setVerifyDialogOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(false);
    setPasswordErrorMessage("");
    setLoading(true);
    try {
      await api.post("/login", { email: email.trim().toLowerCase(), password });
      await refreshAuth();
      const redirectPath = sessionStorage.getItem("redirectAfterLogin");
      sessionStorage.removeItem("redirectAfterLogin");
      navigate(redirectPath || "/console");
    } catch (error: any) {
      console.error("Login failed");
      if (error.response?.status === 500) {
        setPasswordError(true);
        setPasswordErrorMessage(
          "Internal server error. If this issue persists, please contact support."
        );
      } else if (error.response?.status === 403) {
        setPasswordError(true);
        setVerificationError(true);
        setPasswordErrorMessage("Please verify your email");
      } else {
        setPasswordError(true);
        setPasswordErrorMessage("Invalid username or password");
      }
    } finally {
      setLoading(false);
    }
  };

  const validateInputs = () => {
    const email = document.getElementById("email") as HTMLInputElement;
    const password = document.getElementById("password") as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage("Please enter a valid email address.");
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (!password.value) {
      setPasswordError(true);
      setPasswordErrorMessage("Please enter a password.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    return isValid;
  };

  return (
    <AppTheme {...props}>
      <>
        {/* Meta tags for SEO */}
        <Helmet>
          <title>Login | Event Spy</title>
          <meta
            name="description"
            content="Log In to Event Spy | Manage Your Ticket Alerts & Saved Events"
          />
        </Helmet>
      </>
      <CssBaseline enableColorScheme />
      <GradientBackground />
      <Banner showMenuItems={false} />
      <SignInContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            Sign in
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                onChange={(e) => setEmail(e.target.value)}
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={emailError ? "error" : "primary"}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                onChange={(e) => setPassword(e.target.value)}
                name="password"
                placeholder="••••••"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={passwordError ? "error" : "primary"}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      disableRipple
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ bgcolor: "transparent", border: "none" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
              {verificationError && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleVerifyOpen}
                  sx={{ mt: 2 }}
                >
                  Request Verification Email
                </Button>
              )}
            </FormControl>
            <ForgotPassword open={open} handleClose={handleClose} />
            <RequestVerification
              open={verifyDialogOpen}
              handleClose={handleVerifyClose}
            />
            {!verificationError && (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                onClick={validateInputs}
                disabled={loading || !email || !password}
                sx={{
                  "&.Mui-disabled": {
                    color: "black",
                    opacity: 0.7,
                    backgroundColor: "primary.main",
                  },
                }}
              >
                Sign in
              </Button>
            )}
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: "center" }}
            >
              Forgot your password?
            </Link>
          </Box>
          <Divider>or</Divider>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography sx={{ textAlign: "center" }}>
              Don&apos;t have an account?{" "}
              <Link href="/signup" variant="body2" sx={{ alignSelf: "center" }}>
                Sign up
              </Link>
            </Typography>
          </Box>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
};

export default Login;
